/**
 * useVoting Hook
 *
 * Provides reactive access to employee voting system including:
 * - Current voting period
 * - User's vote status
 * - Voting results
 * - Days remaining
 */

import { useState, useEffect } from 'react';
import { db } from '@/firebase';
import { collection, doc, query, where, onSnapshot, orderBy, limit } from 'firebase/firestore';
import { VotingPeriod, Vote, VotingResults } from '@/types';
import type { User } from '@/utils/seedData';
import {
  castVote,
  getDaysRemaining,
  getHoursRemaining,
  maybeCloseEarlyIfAllVoted,
} from '@/services/voting.service';
import { getTenantIdSync, tenantIdReadUnion } from '@/hooks/useTenantId';

interface UseVotingReturn {
  // Current voting period
  votingPeriod: VotingPeriod | null;
  isVotingOpen: boolean;
  daysRemaining: number;
  hoursRemaining: number;

  // User's vote status
  hasVoted: boolean;
  userVote: Vote | null;
  loading: boolean;

  // Actions
  submitVote: (
    nomineeId: string,
    nomineeName: string,
    reason?: string
  ) => Promise<void>;

  // Error handling
  error: Error | null;
}

export function useVoting(
  userId: string | null,
  userName: string,
  tenantId?: string
): UseVotingReturn {
  const [votingPeriod, setVotingPeriod] = useState<VotingPeriod | null>(null);
  const [userVote, setUserVote] = useState<Vote | null>(null);
  const [hasVoted, setHasVoted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [daysRemaining, setDaysRemaining] = useState(0);
  const [hoursRemaining, setHoursRemaining] = useState(0);
  // Resolve to canonical when the caller omits tenantId so every read below
  // carries a tenant filter (required once `voting_*` is isolation-strict).
  const tid = tenantId ?? getTenantIdSync();

  // Listen for current voting period
  useEffect(() => {
    // Union read (NOT strict ==) — see CLAUDE.md "tenantIdReadUnion". Always
    // tenant-filtered now so a future strict isolation rule won't reject it.
    const q = query(
      collection(db, 'voting_periods'),
      where('tenantId', 'in', tenantIdReadUnion(tid)),
      where('status', '==', 'active'),
      orderBy('startDate', 'desc'),
      limit(1)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        if (!snapshot.empty) {
          const doc = snapshot.docs[0];
          const period = {
            id: doc.id,
            ...doc.data(),
          } as VotingPeriod;

          // Verify period is actually active (within date range)
          const now = new Date();
          const start = new Date(period.startDate);
          const end = new Date(period.endDate);

          if (now >= start && now <= end) {
            setVotingPeriod(period);
            setDaysRemaining(getDaysRemaining(period.endDate));
            setHoursRemaining(getHoursRemaining(period.endDate));
          } else {
            setVotingPeriod(null);
          }
        } else {
          setVotingPeriod(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error listening to voting period:', err);
        setError(err instanceof Error ? err : new Error('Failed to load voting period'));
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [tid]);

  // Listen for user's vote in current period
  useEffect(() => {
    if (!userId || !votingPeriod?.id) {
      setUserVote(null);
      setHasVoted(false);
      return;
    }

    const q = query(
      collection(db, 'votes'),
      where('tenantId', 'in', tenantIdReadUnion(tid)),
      where('votingPeriodId', '==', votingPeriod.id),
      where('voterId', '==', userId),
      limit(1)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        if (!snapshot.empty) {
          const doc = snapshot.docs[0];
          const vote = {
            id: doc.id,
            ...doc.data(),
          } as Vote;
          setUserVote(vote);
          setHasVoted(true);
        } else {
          setUserVote(null);
          setHasVoted(false);
        }
      },
      (err) => {
        console.error('Error listening to user vote:', err);
      }
    );

    return unsubscribe;
  }, [userId, votingPeriod?.id, tid]);

  // Update remaining time every minute
  useEffect(() => {
    if (!votingPeriod) return;

    const interval = setInterval(() => {
      setDaysRemaining(getDaysRemaining(votingPeriod.endDate));
      setHoursRemaining(getHoursRemaining(votingPeriod.endDate));
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [votingPeriod]);

  // Submit vote action
  const submitVote = async (
    nomineeId: string,
    nomineeName: string,
    reason?: string
  ): Promise<void> => {
    if (!userId) {
      throw new Error('User not logged in');
    }

    if (!votingPeriod?.id) {
      throw new Error('No active voting period');
    }

    if (hasVoted) {
      throw new Error('You have already voted in this period');
    }

    try {
      await castVote(
        votingPeriod.id,
        userId,
        userName,
        nomineeId,
        nomineeName,
        reason,
        tenantId
      );
      // Real-time listener will update hasVoted automatically.
      // Fire-and-forget early-close check — if this was the last vote needed,
      // the period closes + results calculate immediately, announcement flag flips.
      maybeCloseEarlyIfAllVoted(votingPeriod.id, tenantId).catch((err) => {
        console.error('[voting] Early close check failed:', err);
      });
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to submit vote'));
      throw err;
    }
  };

  return {
    votingPeriod,
    isVotingOpen: !!votingPeriod,
    daysRemaining,
    hoursRemaining,
    hasVoted,
    userVote,
    loading,
    submitVote,
    error,
  };
}

/**
 * Hook for getting all team members (for voting modal)
 *
 * Tenant filtering: deliberately UNFILTERED, matching the app-wide
 * `useTeam` hook in `@/hooks/useTeam`. Many seeded user documents pre-date
 * the tenantId field entirely, and the ones that do carry it can be
 * either 'techery-au' (canonical) or the legacy 'techery'. A strict OR
 * union-read where-clause excludes records with a missing field, which is
 * why the voting nominee picker rendered empty even after switching to
 * `tenantIdReadUnion()`.
 *
 * Single-tenant shop today, so an unfiltered read is safe. We still
 * accept a `tenantId` param for API parity + future re-introduction
 * once a backfill stamps every user document with the canonical id.
 *
 * Active-user filtering happens in the consumer (VotingModal already
 * filters out the current user; everything else is a candidate).
 */
export function useTeamMembers(_tenantId?: string) {
  const [teamMembers, setTeamMembers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, 'users'), orderBy('name', 'asc'));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const members = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }) as User);
        setTeamMembers(members);
        setLoading(false);
      },
      (err) => {
        console.error('Error loading team members:', err);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, []);

  return { teamMembers, loading };
}

/**
 * Hook for getting voting results
 */
export function useVotingResults(votingPeriodId: string | null) {
  const [results, setResults] = useState<VotingResults | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!votingPeriodId) {
      setResults(null);
      setLoading(false);
      return;
    }

    // The results doc id IS the votingPeriodId, so read it directly as a
    // single-doc listener. This replaces a full-collection scan-then-find
    // (which a strict isolation rule would have rejected as an unfiltered
    // list query); single-doc gets are governed per-doc, not as list queries.
    const unsubscribe = onSnapshot(
      doc(db, 'voting_results', votingPeriodId),
      (snapshot) => {
        if (snapshot.exists()) {
          setResults({
            id: snapshot.id,
            ...snapshot.data(),
          } as VotingResults);
        } else {
          setResults(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error loading voting results:', err);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [votingPeriodId]);

  return { results, loading };
}
